import React, { useCallback, useEffect, useState } from 'react'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../ui/Dialog'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'

export interface RenameTarget {
	id: string | number
	name: string
	type: 'playbook' | 'folder' | 'document'
}

interface RenameDialogProps {
	isOpen: boolean
	target: RenameTarget | null
	onClose: () => void
	onRename: (id: string | number, newName: string) => Promise<void>
}

const TYPE_LABELS: Record<RenameTarget['type'], string> = {
	playbook: 'Playbook',
	folder: 'Folder',
	document: 'Document',
}

export function RenameDialog({ isOpen, target, onClose, onRename }: RenameDialogProps) {
	const [newName, setNewName] = useState('')
	const [nameError, setNameError] = useState('')
	const [isRenaming, setIsRenaming] = useState(false)

	// Initialize name when dialog opens with a target
	useEffect(() => {
		if (isOpen && target) {
			setNewName(target.name)
			setNameError('')
			setIsRenaming(false)
		}
	}, [isOpen, target])

	// Validate name
	useEffect(() => {
		if (newName.length === 0) {
			setNameError('')
		} else if (newName.trim().length === 0) {
			setNameError('Name cannot be empty')
		} else if (newName.trim().length > 255) {
			setNameError('Name must be 255 characters or less')
		} else {
			setNameError('')
		}
	}, [newName])

	const handleRename = useCallback(async () => {
		if (!target) return

		const trimmedName = newName.trim()

		// Validate before renaming
		if (!trimmedName) {
			setNameError('Name is required')
			return
		}
		if (trimmedName.length > 255) {
			setNameError('Name must be 255 characters or less')
			return
		}
		// No change needed
		if (trimmedName === target.name) {
			onClose()
			return
		}

		setIsRenaming(true)
		try {
			await onRename(target.id, trimmedName)
			onClose()
		} catch (error) {
			console.error('Failed to rename:', error)
			setNameError('Failed to rename. Please try again.')
		} finally {
			setIsRenaming(false)
		}
	}, [newName, target, onRename, onClose])

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter' && !isRenaming && isFormValid) {
				handleRename()
			}
		},
		[handleRename, isRenaming],
	)

	const handleOpenChange = useCallback(
		(open: boolean) => {
			if (!open && !isRenaming) {
				onClose()
			}
		},
		[onClose, isRenaming],
	)

	const isFormValid = newName.trim().length > 0 && newName.trim().length <= 255 && !nameError
	const typeLabel = target ? TYPE_LABELS[target.type] : 'Item'

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Rename {typeLabel}</DialogTitle>
				</DialogHeader>
				<div className="form-field">
					<label htmlFor="rename-input" className="form-label">
						Name
					</label>
					<Input
						id="rename-input"
						type="text"
						value={newName}
						onChange={e => setNewName(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder={`Enter ${typeLabel.toLowerCase()} name`}
						maxLength={255}
						disabled={isRenaming}
						className={nameError ? 'border-destructive' : ''}
						aria-invalid={!!nameError}
						autoFocus
					/>
					{nameError && (
						<p className="form-error">{nameError}</p>
					)}
				</div>
				<DialogFooter>
					<Button
						variant="ghost"
						onClick={onClose}
						disabled={isRenaming}
					>
						Cancel
					</Button>
					<Button
						variant="action"
						onClick={handleRename}
						disabled={!isFormValid || isRenaming}
					>
						{isRenaming ? 'Renaming...' : 'Rename'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
